import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';

import { EnvironmentClient, WebsiteOriginsModel } from '../../../api/api';

interface Props {
	componentId: string;
	environmentId?: string;
}

export default function WebsiteOriginsEditor({ componentId, environmentId }: Props) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const settings = useQuery({
		queryKey: ['website-origins', componentId, environmentId],
		queryFn: ({ signal }) => environmentId
			? client.getEnvironmentComponentAllowedOrigins(environmentId, componentId, signal)
			: client.getComponentAllowedOrigins(componentId, signal),
	});

	if (settings.isError) {
		return <Typography color="error">Could not load allowed website origins.</Typography>;
	}

	if (!settings.data) {
		return <Typography color="text.secondary">Loading allowed website origins...</Typography>;
	}

	return <WebsiteOriginsForm
		key={JSON.stringify([componentId, environmentId, settings.data])}
		componentId={componentId}
		environmentId={environmentId}
		settings={settings.data}
	/>;
}

function WebsiteOriginsForm({ componentId, environmentId, settings }: Props & {
	settings: WebsiteOriginsModel;
}) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const toast = useToastService();
	const queryClient = useQueryClient();
	const defaultText = settings.defaultOrigins.join('\n');
	const [inherit, setInherit] = useState(!!environmentId && settings.overrideOrigins == null);
	const [text, setText] = useState((settings.overrideOrigins ?? settings.defaultOrigins).join('\n'));
	const save = useMutation({
		mutationFn: async () => {
			const allowedOrigins = inherit ? undefined : text.split('\n').map(origin => origin.trim()).filter(Boolean);
			if (environmentId) {
				await client.setEnvironmentComponentAllowedOrigins(environmentId, componentId, { allowedOrigins });
			} else {
				await client.setComponentAllowedOrigins(componentId, { allowedOrigins });
			}
		},
		onSuccess: async () => {
			toast.success('Website origins saved. Affected deployments will apply the updated policy.');
			await queryClient.invalidateQueries({ queryKey: ['website-origins', componentId] });
		},
		onError: error => toast.error(error.message),
	});

	return (
		<Stack spacing={1.5}>
			<Typography variant="subtitle2">Allowed website origins</Typography>
			{environmentId && (
				<FormControlLabel
					control={<Checkbox checked={inherit} disabled={save.isPending} onChange={event => setInherit(event.target.checked)} />}
					label="Inherit the component default"
				/>
			)}
			<TextField
				label={environmentId ? 'Website origins' : 'Default website origins'}
				multiline
				minRows={2}
				value={inherit ? defaultText : text}
				onChange={event => setText(event.target.value)}
				disabled={inherit || save.isPending}
				placeholder="https://shop.example.com"
				helperText="Enter one origin per line, including the scheme and optional port. An empty list disables CORS."
			/>
			<Stack direction="row" justifyContent="flex-end">
				<Button size="small" variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
					Save website origins
				</Button>
			</Stack>
		</Stack>
	);
}
